import { type FC, useCallback, useState } from "react";

import { Button } from "../../common/Button";
import { type BlackboardInstance } from "../blackboardModels";
import { parseTeamsBindLink, type TeamsBindLink } from "./helpers";
import { InfoRow } from "./InfoRow";
import { TeamsBindLinkPanel } from "./TeamsBindLinkPanel";

// ---------------------------------------------------------------------------
// TeamsInviteSection — mint a Teams bind link for this actor. Only renders
// when the Teams chat SDK is enabled at the org level.
// ---------------------------------------------------------------------------

export const TeamsInviteSection: FC<{
  instance: BlackboardInstance;
  teamsChatSdkEnabled: boolean;
}> = ({ instance, teamsChatSdkEnabled }) => {
  const [teamsInviteLink, setTeamsInviteLink] = useState<TeamsBindLink | null>(
    null,
  );
  const [mintingTeamsInvite, setMintingTeamsInvite] = useState(false);

  const handleMintTeamsInvite = useCallback(async () => {
    setMintingTeamsInvite(true);
    try {
      const resp = await fetch(
        `/user/instances/${instance.id}/teams-invite-token`,
        { credentials: "include", method: "POST" },
      );
      if (!resp.ok) {
        throw new Error(`Failed to mint Teams invite (${resp.status})`);
      }
      setTeamsInviteLink(parseTeamsBindLink(await resp.json()));
    } finally {
      setMintingTeamsInvite(false);
    }
  }, [instance.id]);

  if (!teamsChatSdkEnabled) {
    return null;
  }

  return (
    <InfoRow label="Teams">
      <div className="flex flex-col gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={mintingTeamsInvite || instance.status === "killed"}
          onClick={handleMintTeamsInvite}
        >
          {mintingTeamsInvite ? "Minting…" : "Invite to Teams"}
        </Button>
        {teamsInviteLink && <TeamsBindLinkPanel link={teamsInviteLink} />}
      </div>
    </InfoRow>
  );
};
